import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWebsiteContent } from '../WebsiteContentContext';

const Star = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 6.96H22l-6.18 4.49L18.21 21 12 16.27 5.79 21l2.39-7.55L2 8.96h7.61z" /></svg>
);

const HeroSlider = () => {
  const { hero_slides = [] } = useWebsiteContent();
  const slides = hero_slides;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((i) => setIndex((i + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused || slides.length < 2) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [paused, next, slides.length]);

  if (slides.length === 0) return null;

  return (
    <section
      data-testid="hero-slider"
      className="relative h-screen min-h-[640px] w-full overflow-hidden bg-slate-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((s, i) => (
        <div
          key={i}
          data-testid={`hero-slide-${i}`}
          className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={s.image}
            alt={s.eyebrow}
            className="absolute inset-0 w-full h-full object-cover scale-105"
            style={{ transform: i === index ? 'scale(1)' : 'scale(1.08)', transition: 'transform 8s ease-out' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full">
              <div className="max-w-2xl text-white">
                <span className={`inline-block text-xs uppercase tracking-[0.25em] font-semibold text-blue-300 mb-5 transition-all duration-700 delay-100 ${i === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>{s.eyebrow}</span>
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight transition-all duration-700 delay-200 ${i === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>{s.title}</h1>
                <p className={`mt-6 text-lg lg:text-xl text-slate-200 max-w-xl leading-relaxed transition-all duration-700 delay-300 ${i === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>{s.subtitle}</p>
                <div className={`mt-10 flex flex-wrap gap-3 transition-all duration-700 delay-500 ${i === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <Link to={s.cta_to || '/register'} data-testid={`hero-cta-${i}`} className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-600/40 transition">
                    {s.cta_label}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold backdrop-blur border border-white/20 transition">Book a demo</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button type="button" onClick={prev} data-testid="hero-prev" aria-label="Previous slide" className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur border border-white/20 text-white flex items-center justify-center transition">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <button type="button" onClick={next} data-testid="hero-next" aria-label="Next slide" className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur border border-white/20 text-white flex items-center justify-center transition">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-3">
        {slides.map((_, i) => (
          <button type="button" key={i} onClick={() => go(i)} data-testid={`hero-dot-${i}`} aria-label={`Go to slide ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-10 bg-white' : 'w-5 bg-white/40 hover:bg-white/70'}`} />
        ))}
      </div>
    </section>
  );
};

const ClientLogos = () => {
  const { client_logos = [], client_logos_label = '' } = useWebsiteContent();
  return (
    <section data-testid="client-logos" className="py-16 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 mb-10">{client_logos_label}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
          {client_logos.map((c, i) => (
            <div key={i} data-testid={`client-logo-${i}`} className="h-14 flex items-center text-slate-400 hover:text-slate-700 font-serif italic text-lg lg:text-base xl:text-lg transition tracking-tight">{c.name}</div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FeatureHighlights = () => {
  const { features_eyebrow, features_title, features = [] } = useWebsiteContent();
  return (
    <section data-testid="feature-highlights" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600">{features_eyebrow}</span>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.1]">{features_title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div key={i} data-testid={`feature-${i}`} className="group p-8 rounded-2xl border border-slate-200 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100/50 transition">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{f.title}</h3>
              <p className="text-slate-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorksStrip = () => {
  const { how_it_works = [] } = useWebsiteContent();
  return (
    <section data-testid="how-it-works-strip" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {how_it_works.map((s, i) => (
            <div key={i} data-testid={`how-step-${i}`} className="relative">
              <div className="text-5xl font-bold text-blue-600/20 mb-2">{s.step}</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{s.title}</h3>
              <p className="text-slate-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { testimonials_eyebrow, testimonials_title, testimonials = [] } = useWebsiteContent();
  return (
    <section data-testid="testimonials" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600">{testimonials_eyebrow}</span>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.1]">{testimonials_title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <figure key={i} data-testid={`testimonial-${i}`} className="p-8 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col">
              <div className="flex gap-1 text-amber-400 mb-5">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-4 h-4" />)}
              </div>
              <blockquote className="text-slate-700 leading-relaxed flex-1">"{t.quote}"</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 pt-6 border-t border-slate-200">
                <div className="w-11 h-11 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center text-sm">{t.initials || (t.name || '?').slice(0, 2).toUpperCase()}</div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                  <div className="text-slate-500 text-xs">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { final_cta_title, final_cta_subtitle } = useWebsiteContent();
  return (
    <section data-testid="final-cta" className="py-24 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3) 0, transparent 50%)' }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-[1.1]">{final_cta_title}</h2>
          <p className="mt-5 text-lg text-blue-100 max-w-xl">{final_cta_subtitle}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register" data-testid="final-cta-register" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-blue-700 font-semibold shadow-lg hover:bg-blue-50 transition">Start Free Trial</Link>
            <Link to="/contact" data-testid="final-cta-contact" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/30 backdrop-blur transition">Talk to sales</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Home = () => (
  <div data-testid="page-home">
    <HeroSlider />
    <ClientLogos />
    <FeatureHighlights />
    <HowItWorksStrip />
    <Testimonials />
    <FinalCTA />
  </div>
);

export default Home;
